import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarClock, Heart, Home, Flame } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { MyDayTab } from "@/components/rhythm/MyDayTab";
import { MeTab } from "@/components/rhythm/MeTab";
import { UsTab } from "@/components/rhythm/UsTab";
import { MilestoneBadge } from "@/components/rhythm/MilestoneBadge";
import { localDate } from "@/lib/dates";

const SAGE = { bg: "#F7F9F7", primary: "#4A6B5D", text: "#2D3732", border: "#D1DCD6" };

export default function RhythmPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("my-day");
  const [reflectionPrompt, setReflectionPrompt] = useState(null);
  const [starterRequested, setStarterRequested] = useState(0);
  const [timeline, setTimeline] = useState([]);
  const [personalized, setPersonalized] = useState(true);
  const [dayMode, setDayMode] = useState(() => ([0, 6].includes(new Date().getDay()) ? "weekend" : "weekday"));
  const [completedToday, setCompletedToday] = useState([]);
  const [streak, setStreak] = useState(null);
  const [recap, setRecap] = useState(null);
  const [milestone, setMilestone] = useState(null);

  const onMilestone = (m) => {
    if (m) {
      setMilestone({ days: m.days, bonusPoints: m.bonus?.points_earned || null });
      fetchStreak();
    }
  };

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchStreak = useCallback(async () => {
    try {
      const [streakRes, recapRes] = await Promise.all([
        axios.get(`${API}/rhythm/streak?date=${localDate()}`, authHeaders),
        axios.get(`${API}/rhythm/weekly-recap?date=${localDate()}`, authHeaders),
      ]);
      setStreak(streakRes.data);
      setRecap(recapRes.data);
    } catch (e) {
      console.error("Failed to load streak:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rhythm/timeline?mode=${dayMode}&date=${localDate()}`, authHeaders);
      setTimeline(res.data.items || []);
      setCompletedToday(res.data.completed_today || []);
      setPersonalized(res.data.personalized !== false);
    } catch (e) {
      console.error("Failed to load timeline:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dayMode]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  useEffect(() => {
    fetchStreak();
  }, [fetchStreak, activeTab]);

  // Cross-tab trigger: dinner prep -> conversation starter on Us
  const openDinnerConversation = () => {
    setStarterRequested((n) => n + 1);
    setActiveTab("us");
  };

  // Cross-tab trigger: shutdown -> reflection on Me
  const openShutdownReflection = () => {
    setReflectionPrompt("As the day closes: what's one thing you're letting go of, and one thing you're grateful for?");
    setActiveTab("me");
  };

  const tabs = [
    { id: "my-day", label: "My Day", icon: CalendarClock },
    { id: "me", label: "Me", icon: Heart },
    { id: "us", label: "Us", icon: Home },
  ];

  return (
    <div className="min-h-screen font-body" style={{ backgroundColor: SAGE.bg, color: SAGE.text }} data-testid="rhythm-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-md border-b" style={{ backgroundColor: "rgba(247,249,247,0.85)", borderColor: SAGE.border }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 rounded-full hover:bg-black/5 transition-colors"
            data-testid="rhythm-back-btn"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: SAGE.primary }} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold font-body tracking-tight" style={{ color: SAGE.primary }}>Daily Rhythm</h1>
            <p className="text-xs opacity-70">Your day, your alignment, your home</p>
          </div>
          {streak && streak.streak > 0 && (
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{
                backgroundColor: streak.active_today ? SAGE.primary : `${SAGE.primary}15`,
                color: streak.active_today ? "#FFFFFF" : SAGE.primary,
              }}
              onClick={() => {
                if (streak.milestones?.length > 0) {
                  setMilestone({ days: Math.max(...streak.milestones), bonusPoints: null });
                }
              }}
              title={streak.active_today ? "Streak secured for today" : "Complete a rhythm moment to keep your streak"}
              data-testid="rhythm-streak"
            >
              <Flame className="w-3.5 h-3.5" style={{ color: streak.active_today ? "#FCD34D" : "#D97706" }} />
              {streak.streak}-day streak
            </button>
          )}
        </div>
      </header>

      {/* Tab content */}
      <main className="max-w-lg mx-auto px-4 pt-4 pb-28">
        {activeTab === "my-day" && (
          <MyDayTab
            timeline={timeline}
            recap={recap}
            personalized={personalized}
            dayMode={dayMode}
            onDayModeChange={setDayMode}
            completedToday={completedToday}
            onCompleted={(id) => {
              setCompletedToday((prev) => [...prev, id]);
              fetchStreak();
            }}
            onRefresh={fetchTimeline}
            token={token}
            onDinnerConversation={openDinnerConversation}
            onShutdownReflection={openShutdownReflection}
            onMilestone={onMilestone}
            palette={SAGE}
          />
        )}
        {activeTab === "me" && (
          <MeTab
            token={token}
            reflectionPrompt={reflectionPrompt}
            onReflectionUsed={() => setReflectionPrompt(null)}
            onMilestone={onMilestone}
            streakInfo={streak}
            onViewBadge={(days) => setMilestone({ days, bonusPoints: null })}
            palette={SAGE}
          />
        )}
        {activeTab === "us" && (
          <UsTab token={token} starterRequested={starterRequested} onMilestone={onMilestone} palette={SAGE} />
        )}
      </main>

      {milestone && (
        <MilestoneBadge
          days={milestone.days}
          bonusPoints={milestone.bonusPoints}
          onClose={() => setMilestone(null)}
          palette={SAGE}
        />
      )}

      {/* Fixed bottom navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t"
        style={{ backgroundColor: "#FFFFFF", borderColor: SAGE.border }}
        data-testid="rhythm-bottom-nav"
      >
        <div className="max-w-lg mx-auto grid grid-cols-3">
          {tabs.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="flex flex-col items-center gap-1 py-3 transition-colors"
                style={{ color: active ? SAGE.primary : "#8A968F" }}
                data-testid={`rhythm-tab-${id}`}
              >
                <Icon className="w-6 h-6" strokeWidth={active ? 2.2 : 1.6} />
                <span className={`text-xs ${active ? "font-bold" : "font-medium"}`}>{label}</span>
                {active && <span className="w-8 h-1 rounded-full -mb-1" style={{ backgroundColor: SAGE.primary }} />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
